import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/hooks/useToast';
import * as seguimientosApi from '@/api/seguimientos.api';

/**
 * DEMO (tanda 3, ola B): Richard le escribe al número de avisos desde su teléfono
 * ficticio, como en producción. «1» aprueba los borradores de hoy y «2» manda el
 * enlace. La respuesta también aparece en su «Teléfono de Richard», que se refresca solo.
 */
export function EscribirComoRichardView({
  texto,
  onTexto,
  onEnviar,
  onPlanificar,
  ocupado,
  respuesta,
}: {
  texto: string;
  onTexto: (t: string) => void;
  onEnviar: () => void;
  onPlanificar: () => void;
  ocupado: boolean;
  respuesta: string | null;
}) {
  return (
    <div className="space-y-2 border-t border-surface-border p-3 dark:border-slate-800">
      <p className="text-xs font-medium text-slate-600 dark:text-slate-300">Escribir como Richard (número de avisos)</p>
      <p className="text-xs text-slate-400">1 aprueba los borradores de hoy · 2 manda el enlace a Seguimientos</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (texto.trim()) onEnviar();
        }}
        className="flex gap-2"
      >
        <input
          value={texto}
          onChange={(e) => onTexto(e.target.value)}
          placeholder="1"
          aria-label="Mensaje de Richard al número de avisos"
          className="h-8 flex-1 rounded-md border border-surface-border bg-surface-muted px-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
        />
        <Button type="submit" size="sm" disabled={ocupado || !texto.trim()}>
          Enviar
        </Button>
      </form>
      {respuesta && <p className="rounded-md bg-slate-50 p-2 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200">{respuesta}</p>}
      <Button variant="outline" size="sm" disabled={ocupado} onClick={onPlanificar}>
        Planificar ahora
      </Button>
    </div>
  );
}

export function EscribirComoRichard() {
  const qc = useQueryClient();
  const toast = useToast();
  const [texto, setTexto] = useState('');
  const [respuesta, setRespuesta] = useState<string | null>(null);
  const enviar = useMutation({
    mutationFn: () => seguimientosApi.escribirComoRichard(texto.trim()),
    onSuccess: (r) => {
      setTexto('');
      setRespuesta(r.respuesta ?? 'Guardado: ese mensaje no lleva respuesta.');
      void qc.invalidateQueries({ queryKey: ['seguimientos'] });
      // La respuesta del interceptor llega al «Teléfono de Richard»: se refresca ya, sin esperar 3 s.
      void qc.invalidateQueries({ queryKey: ['simulador', 'avisos'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo escribir como Richard'),
  });
  const planificar = useMutation({
    mutationFn: () => seguimientosApi.planificarDemo(),
    onSuccess: (r) => {
      toast.success(`Listo: ${r.planificados} seguimiento(s) nuevos o movidos; ${r.reclasificadas} temperatura(s) al día.`);
      void qc.invalidateQueries({ queryKey: ['seguimientos'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo planificar'),
  });
  return (
    <EscribirComoRichardView
      texto={texto}
      onTexto={setTexto}
      onEnviar={() => enviar.mutate()}
      onPlanificar={() => planificar.mutate()}
      ocupado={enviar.isPending || planificar.isPending}
      respuesta={respuesta}
    />
  );
}
